import { ForwardedRef, useImperativeHandle, useState } from 'react';
import { StepItemProps, StepperProps, StepperRef } from './interface';

const getCompletedStepItems = (stepItems: StepItemProps[]) => {
  const filteredList = stepItems.filter((item) => item.complete);
  if (!filteredList.length) {
    return [-1];
  }
  return filteredList.map((_, i) => i);
};

const getState = (
  idx: number,
  stepItems: StepItemProps[],
  completedList: number[]
) => {
  if (stepItems[idx]?.disabled) return 'disabled';
  if (completedList.includes(idx)) return 'complete';
  const activeItems = stepItems.findIndex(
    (t, i) => !completedList.includes(i) && !t.disabled
  );
  return activeItems === idx ? 'active' : 'inactive';
};

export const useStepper = (
  props: StepperProps,
  stepperRef: ForwardedRef<StepperRef>
) => {
  const { children, onActiveChange, onComplete, orientation } = props;
  const stepItems = children.map((c) => c.props);
  const [animateState, setAnimateState] = useState('next');

  const [completedList, setCompletedList] = useState(
    getCompletedStepItems(stepItems)
  );

  const completeIndex = completedList.sort()[completedList.length - 1];

  useImperativeHandle(
    stepperRef,
    () => ({
      next: () => {
        const nextIndex =
          completeIndex === stepItems.length
            ? stepItems.length - 1
            : completeIndex + 1;

        const actualIndex = stepItems[nextIndex].disabled
          ? nextIndex + 1
          : nextIndex;

        setCompletedList([...completedList, actualIndex]);
        stepItems?.[completeIndex]?.onComplete?.();

        onActiveChange?.(actualIndex);

        setAnimateState('next');

        if (nextIndex === stepItems.length - 1) {
          onComplete?.();
        }
      },
      prev: () => {
        const prevIndex =
          completeIndex === stepItems.length - 1
            ? [completeIndex, completeIndex - 1]
            : [completeIndex];
        const currentList = completedList.filter((t) => !prevIndex.includes(t));
        setCompletedList(currentList);
        setAnimateState('prev');
      }
    }),
    [completeIndex, completedList, stepItems, onActiveChange, onComplete]
  );

  const updatedStepItems = stepItems.map((item, i) => {
    const direction =
      i === 0 || (orientation === 'vertical' && i !== stepItems.length - 1)
        ? 'end'
        : i === stepItems.length - 1
        ? 'start'
        : 'both';

    const state = getState(i, stepItems, completedList);

    return {
      ...item,
      content: item.children,
      itemsType: props.itemsType,
      icon: item.activeState === 'warning' ? undefined : item.icon,
      _compeleteDivider: i >= completeIndex && orientation === 'horizontal',
      direction,
      index: i + 1,
      state,
      _ariaCurrent: state === 'active' ? 'step' : undefined
    };
  });

  return {
    stepItems: updatedStepItems,
    completeIndex,
    animateState
  };
};
